/**
 * @copyright 2025 Cooltrax Pty Ltd. All rights reserved.
 */

import React, { useRef, useState } from 'react'

/** Icons only: the width of one icon column. */
export const COLLAPSED_WIDTH = 64
/** Dragged narrower than this, the sidebar snaps to icons; wider, it opens again. */
export const COLLAPSE_BELOW = 160
export const MIN_WIDTH = 200
export const MAX_WIDTH = 480
export const DEFAULT_WIDTH = 256
const KEY_STEP = 16

export const isCollapsedWidth = (width: number): boolean => width <= COLLAPSED_WIDTH

/** Where a drag or key press to `raw` pixels leaves the sidebar: icons, or a width in range. */
export const nextSidebarWidth = (raw: number): number =>
  raw < COLLAPSE_BELOW ? COLLAPSED_WIDTH : Math.round(Math.min(MAX_WIDTH, Math.max(MIN_WIDTH, raw)))

interface SidebarProps {
  /** Pixels; COLLAPSED_WIDTH or less shows icons only. */
  width: number
  /** Called once a drag ends, on double-click and on arrow keys, with the new width. */
  onWidthChange: (width: number) => void
  /** Pinned above the scrolling children, such as the logo. */
  header?: React.ReactNode
  children: React.ReactNode
  className?: string
}

/**
 * A left sidebar sized by dragging its right edge. Narrower than COLLAPSE_BELOW it snaps to
 * icons; double-clicking the edge toggles between icons and the default width.
 */
const Sidebar: React.FC<SidebarProps> = ({ width, onWidthChange, header, children, className = '' }) => {
  const containerRef = useRef<HTMLDivElement>(null)
  // The width while dragging, kept here so storage is written once, when the drag ends.
  const [dragWidth, setDragWidth] = useState<number>()
  const shown = dragWidth ?? width

  const widthAt = (clientX: number) =>
    nextSidebarWidth(clientX - (containerRef.current?.getBoundingClientRect().left ?? 0))

  const handlePointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    if (e.button !== 0) return
    e.preventDefault()
    e.currentTarget.setPointerCapture(e.pointerId)
    document.body.style.userSelect = 'none'
    setDragWidth(shown)
  }

  const handlePointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    if (dragWidth === undefined) return
    setDragWidth(widthAt(e.clientX))
  }

  const handlePointerUp = (e: React.PointerEvent<HTMLDivElement>) => {
    if (dragWidth === undefined) return
    if (e.currentTarget.hasPointerCapture(e.pointerId)) e.currentTarget.releasePointerCapture(e.pointerId)
    document.body.style.userSelect = ''
    setDragWidth(undefined)
    if (dragWidth !== width) onWidthChange(dragWidth)
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return
    e.preventDefault()
    const step = e.key === 'ArrowRight' ? KEY_STEP : -KEY_STEP
    // From icons, a step right opens the sidebar at its narrowest.
    onWidthChange(isCollapsedWidth(width) ? (step > 0 ? MIN_WIDTH : width) : nextSidebarWidth(width + step))
  }

  const dragging = dragWidth !== undefined

  return (
    <div
      ref={containerRef}
      className={`relative shrink-0 bg-white border-r border-base-300 flex flex-col ${
        dragging ? '' : 'transition-[width] duration-200'
      } ${className}`}
      style={{ width: shown }}
    >
      {header}
      <div className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden pb-4">{children}</div>

      <div
        role="separator"
        aria-orientation="vertical"
        aria-label="Resize sidebar"
        aria-valuenow={shown}
        aria-valuemin={COLLAPSED_WIDTH}
        aria-valuemax={MAX_WIDTH}
        tabIndex={0}
        title="Drag to resize, double-click to collapse"
        className={`absolute top-0 -right-0.5 z-10 w-1 h-full cursor-col-resize transition-colors hover:bg-primary focus-visible:bg-primary outline-none ${
          dragging ? 'bg-primary' : 'bg-transparent'
        }`}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerCancel={handlePointerUp}
        onDoubleClick={() => onWidthChange(isCollapsedWidth(width) ? DEFAULT_WIDTH : COLLAPSED_WIDTH)}
        onKeyDown={handleKeyDown}
      />
    </div>
  )
}

export default Sidebar
